import React from 'react'
import { useTranslation } from 'react-i18next'
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined'

import AnalysisEmptyState from './AnalysisEmptyState'
import './security-tree-outline.css'

/* eslint-disable @typescript-eslint/no-explicit-any */
type ExistingControl = {
  _id?: string
  Label?: string
  Description?: string
}

type SecurityControlNode = {
  _id?: string
  Label?: string
  Description?: string
  ExistingControls?: ExistingControl[]
}

type SecurityTreeData = {
  Label?: string
  SecurityControls?: SecurityControlNode[]
}

/** Vertical, page-breakable rendering of the security tree for PDF export. */
export function SecurityTreeOutline({
  data,
}: {
  data?: SecurityTreeData | any
}) {
  const { t } = useTranslation()
  const controls: SecurityControlNode[] = data?.SecurityControls || []

  if (!controls.length) {
    return (
      <AnalysisEmptyState
        title={t('charts.securityTreeSection.empty')}
        hint={t('charts.securityTreeSection.emptyHint')}
      />
    )
  }

  const existingCount = controls.reduce(
    (sum, c) => sum + (c.ExistingControls?.length || 0),
    0
  )
  const uncovered = controls.filter((c) => !c.ExistingControls?.length).length

  return (
    <div className="sto">
      <div className="sto-root avoid-break">
        <span className="sto-root-mark">
          <AccountTreeOutlined sx={{ fontSize: 18 }} />
        </span>
        <div className="sto-root-text">
          <p className="sto-root-eyebrow">
            {t('charts.securityTreeSection.family')}
          </p>
          <h4>{data?.Label || '—'}</h4>
        </div>
        <div className="sto-stats">
          <span>
            <strong>{controls.length}</strong>
            {t('charts.securityTreeSection.controls', {
              count: controls.length,
            })}
          </span>
          <span>
            <strong>{existingCount}</strong>
            {t('charts.securityTreeSection.existing', {
              count: existingCount,
            })}
          </span>
          <span className={uncovered ? 'is-warn' : ''}>
            <strong>{uncovered}</strong>
            {t('charts.securityTreeSection.uncovered', { count: uncovered })}
          </span>
        </div>
      </div>

      <ol className="sto-branches">
        {controls.map((control, i) => {
          const leaves = control.ExistingControls || []
          return (
            <li
              className={`sto-node${leaves.length ? '' : ' is-uncovered'}`}
              key={control._id || i}
            >
              <div className="sto-card avoid-break">
                <div className="sto-card-head">
                  <span className="sto-code">{i + 1}</span>
                  <div className="sto-card-text">
                    <p className="sto-title">{control.Label || '—'}</p>
                    {control.Description ? (
                      <p className="sto-desc">{control.Description}</p>
                    ) : null}
                  </div>
                  <span
                    className={`sto-pill${leaves.length ? '' : ' is-warn'}`}
                  >
                    {leaves.length
                      ? t('charts.securityTreeSection.existingShort', {
                          count: leaves.length,
                        })
                      : t('charts.securityTreeSection.noCoverage')}
                  </span>
                </div>

                {leaves.length ? (
                  <ul className="sto-leaves">
                    {leaves.map((leaf, j) => (
                      <li className="sto-leaf" key={leaf._id || j}>
                        <span className="sto-leaf-dot" />
                        <div>
                          <p className="sto-leaf-title">{leaf.Label || '—'}</p>
                          {leaf.Description ? (
                            <p className="sto-leaf-desc">{leaf.Description}</p>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

export default SecurityTreeOutline
